// MMC: para fechas en español
import 'dayjs/locale/es';
import { ActionIcon, AppShell, Burger, Group, Header, MantineProvider, Navbar } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { useColorScheme, useHotkeys, useLocalStorage } from '@mantine/hooks';
import { SpotlightProvider } from '@mantine/spotlight';
import { ColorScheme, ColorSchemeProvider, useMantineTheme } from '@mantine/styles';
import { ColorConfigurationValue, DateInputFieldDefaultProps, GoogleMapsAPILoaderConfig, GridGlobalStyles, LoginModal, MenuNavBarPanel, MicroMClient, MicroMRouter, ModalsManager, useIsLoggedIn, UseLocaleFormatDefaultProps, useMenuContent, useViewportDynamicVariables } from '@mcurros2/microm';
import { IconMoonStars, IconSun } from '@tabler/icons-react';
import { ReactNode, useEffect, useState } from 'react';
import { getMenusAndRedirectToHome } from '../../Components/LoggedInMenu/getMenusAndRedirectToHome';
import { LoggedInMenu } from '../../Components/LoggedInMenu/LoggedInMenu';
import { Logo } from '../../Components/Logo';
import { MTSClaimTypes } from '../../Components/MTSClaimTypes';
import { API_URL, APP_ID, GOOGLE_MAPS_API_KEY } from '../../config';
import { PerfilUsuario } from '../../Entities/PerfilUsuario/PerfilUsuario';
import backgroundImage from '../../html/images/background4.jpg';
import { MENU_ID, MenuContent } from '../../MenuContent/BackofficeMenuContent';
import { Translate } from '../../spanish';

const client = new MicroMClient({ app_id: APP_ID, api_url: API_URL, redirect_on_401: "/" });

// MMC: descomentar para habilitar grabación de accesos para los menues
//client.startRecordPaths();

DateInputFieldDefaultProps.valueFormat = 'DD/MM/YYYY';

Translate();

function App() {
    const theme = useMantineTheme();
    const [themeColor, setThemeColor] = useState<ColorConfigurationValue>({ colorKey: theme.primaryColor, colorShade: theme.primaryShade });
    const preferredColorScheme = useColorScheme();

    GoogleMapsAPILoaderConfig.apiKey = GOOGLE_MAPS_API_KEY;
    GoogleMapsAPILoaderConfig.language = 'es-419';

    // MMC: Esto es para que use el formato de fecha en español
    // TODO: Hacer componente para que se pueda elegir el idioma
    UseLocaleFormatDefaultProps.initialLocale = 'es-AR';

    // MMC: initial menu state
    const getInitialNavbarState = () => {
        return window.innerWidth >= 768; // Adjust the breakpoint as needed
    };

    const [opened, setOpened] = useState(getInitialNavbarState());
    const [content, setContent] = useState<ReactNode>(<></>);
    const { isLoggedIn, setIsLoggedIn, loggedInInfo } = useIsLoggedIn({ client });
    const [sidebarOpened, setSidebarOpened] = useState(getInitialNavbarState());

    // MMC: Aqui se indica el menu ID que luego se usa para deshabilitar/habilitar menues. Ver MenuContent
    const menu = useMenuContent({ client, menuContent: MenuContent, setContent, setOpened, isLoggedIn, setIsLoggedIn, menuId: MENU_ID });

    const [colorScheme, setColorScheme] = useLocalStorage({
        key: 'mantine-color-scheme',
        defaultValue: preferredColorScheme,
        getInitialValueInEffect: true,
    });

    const toggleColorScheme = (value?: ColorScheme) => {
        setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'))
    };

    useHotkeys([['mod+J', () => toggleColorScheme()]]);

    const modalOverlayProps = {
        overlayProps: {
            color: colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[9],
            opacity: 0.55,
            blur: 3,
        }
    };

    const [photoURL, setPhotoURL] = useState<string>();
    const [mtsClaims, setMtsClaims] = useState<MTSClaimTypes>();
    const [enabledMenus, setEnabledMenus] = useState<Set<string>>();

    // MMC: para arreglar el problema de que el viewport no se actualiza correctamente en dispositivos móviles IPHONE
    useViewportDynamicVariables();

    useEffect(() => {
        let isMounted = true;

        const checkBackoffice = async () => {
            try {
                const loggedIn = await client.isLoggedInLocal();
                if (!loggedIn) {
                    await client.localLogoff();
                }

                const menus: Set<string> = await client.getMenus();
                if (isMounted) {
                    setEnabledMenus(menus);
                }


                const mts_claims: Partial<MTSClaimTypes> | undefined = loggedInInfo;
                const photo_url = mts_claims?.vc_fotoguid ? client.getThumbnailURL(mts_claims.vc_fotoguid) : undefined;
                setPhotoURL(photo_url);
                setMtsClaims(mts_claims as MTSClaimTypes);

                await getMenusAndRedirectToHome(client);
            } catch (error) {
                console.error("Error getting menus:", error);
            }
        };

        checkBackoffice();

        return () => {
            isMounted = false;
        };
    }, [loggedInInfo]);

    return (
        <MicroMRouter>
            <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
                <MantineProvider theme={{
                    colors: {
                        brand: ["#FCEFE9", "#F6D1C1", "#F0B499", "#EA9771", "#E47A49", "#DE5C21", "#B24A1A", "#853714", "#59250D", "#2C1207"], //https://themera.vercel.app/</ColorSchemeProvider>
                    },
                    colorScheme: colorScheme,
                    focusRing: "auto",
                    primaryColor: themeColor.colorKey,
                    primaryShade: themeColor.colorShade
                    // primaryColor: "brand",
                    // primaryShade: {light: 4, dark: 4}
                }} withCSSVariables withGlobalStyles withNormalizeCSS>
                    <GridGlobalStyles />
                    <SpotlightProvider actions={menu.actions} size="xl">
                        <DatesProvider settings={{ locale: 'es', firstDayOfWeek: 1 }}>
                            <ModalsManager modalProps={modalOverlayProps} animationDuration={0}>
                                {isLoggedIn === false &&
                                    <div style={{
                                        backgroundImage: `url(${backgroundImage})`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'center',
                                        backgroundAttachment: 'fixed',
                                        backgroundSize: 'cover',
                                        height: '100dvh',
                                        padding: '0px',
                                    }}>
                                        <LoginModal client={client} checkOnInit={false} onClose={() => null} onLoggedIn={async () => {
                                            setIsLoggedIn(true);
                                            await getMenusAndRedirectToHome(client);
                                        }} />
                                    </div>
                                }
                                {isLoggedIn === true &&
                                    <AppShell
                                        navbarOffsetBreakpoint="sm"
                                        navbar={
                                            <Navbar p="md" hiddenBreakpoint="sm" hidden={!opened} width={{ sm: 200, lg: 300 }} style={!opened ? { display: 'none', width: 0 } : undefined}>
                                                <MenuNavBarPanel setOpened={setOpened} setContent={setContent} menuItems={menu.items} activeIDState={menu.activeIDState} subitemActiveIDState={menu.subitemActiveIDState} />
                                            </Navbar>
                                        }
                                        header={
                                            <Header height={{ base: 50, md: 70 }} zIndex={101}>
                                                <Group sx={{ height: '100%', display: "flex", justifyContent: "flex-start" }} px={20} position="apart">
                                                    <Burger
                                                        opened={opened}
                                                        onClick={() => setOpened((o) => !o)}
                                                        size="xs"
                                                        color={theme.colors.gray[6]}
                                                        mr="xs"
                                                    />

                                                    <Group w="3rem" h="auto" mr="xs"><Logo /></Group>
                                                    <Group sx={{ flex: 1, justifyContent: "flex-end" }}>
                                                        <LoggedInMenu
                                                            loggedInUser={mtsClaims?.username}
                                                            initials={mtsClaims?.userinitials}
                                                            photoURL={photoURL}
                                                            enabledMenus={enabledMenus}
                                                            onLogOut={async () => {
                                                                await client.logoff();
                                                                setIsLoggedIn(false);
                                                                window.location.href = "/";
                                                            }}
                                                            onEditProfile={async (openform) => {
                                                                if (!mtsClaims) return;
                                                                const perfil = new PerfilUsuario(client);
                                                                perfil.def.columns.vc_fotoguid.value = mtsClaims.vc_fotoguid || '';
                                                                perfil.def.columns.vc_apellido.value = mtsClaims.vc_apellido || '';
                                                                perfil.def.columns.vc_nombre.value = mtsClaims.vc_nombre || '';
                                                                perfil.def.columns.vc_username.value = mtsClaims.username || '';

                                                                await openform({
                                                                    entity: perfil,
                                                                    initialFormMode: 'add',
                                                                    title: 'Editar',
                                                                    onModalSaved: async () => {
                                                                        if (!mtsClaims) return;
                                                                        mtsClaims.vc_fotoguid = perfil.def.columns.vc_fotoguid.value;
                                                                        await client.updateClientClaims(mtsClaims);
                                                                        var photo_url = mtsClaims.vc_fotoguid ? client.getThumbnailURL(mtsClaims.vc_fotoguid) : undefined;
                                                                        setPhotoURL(photo_url);
                                                                    },
                                                                });

                                                            }}
                                                        />
                                                        <ActionIcon variant="default" onClick={() => toggleColorScheme()} size={30}>
                                                            {colorScheme === 'dark' ? <IconSun size="1rem" /> : <IconMoonStars size="1rem" />}
                                                        </ActionIcon>
                                                    </Group>
                                                </Group>
                                            </Header>
                                        }
                                        styles={{
                                            main: {
                                                paddingLeft: !opened ? '1rem' : undefined,
                                                paddingRight: !sidebarOpened ? '1rem' : undefined,
                                                minHeight: 'calc(var(--dynVH, 1vh) * 100)',
                                                width: '100%' //'calc(var(--dynVH, 1vh) * 100)'
                                            }
                                        }}
                                    >
                                        {content}
                                    </AppShell>
                                }
                            </ModalsManager>
                        </DatesProvider>
                    </SpotlightProvider>
                </MantineProvider>
            </ColorSchemeProvider >
        </MicroMRouter >
    );
}

export default App;
